import { useEffect, useState } from "react";
import { Hand, Headphones, Trophy } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { toast } from "sonner";

export const ListenBoard = ({ onClose, roomId = "" }) => {
  const [board, setBoard] = useState(null);
  const [cheered, setCheered] = useState([]);

  const cheer = async (person) => {
    if (!person.room_id) {
      toast.error("Start a chat with them first");
      return;
    }
    try {
      await api.post(`/rooms/${person.room_id}/messages`, {
        text: `${person.name}, your listening run is slipping — one story gets you going again!`,
        kind: "cheer",
      });
      setCheered((all) => [...all, person.name]);
      toast.success(`Cheer sent to ${person.name}`);
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail) || "Could not send that cheer");
    }
  };

  useEffect(() => {
    api.get(`/news/listen-streak/board${roomId ? `?room_id=${roomId}` : ""}`)
      .then(({ data }) => setBoard(data)).catch(() => {});
  }, [roomId]);

  if (!board) return null;

  return (
    <div className="mt-2 rounded-2xl border border-white/10 bg-panel p-3" data-testid="listen-board">
      <div className="flex items-center justify-between gap-2">
        <p className="inline-flex items-center gap-1.5 text-xs font-semibold text-brand">
          <Trophy className="h-3.5 w-3.5" /> Listening runs · you and {board.friends} other
          {board.friends === 1 ? "" : "s"}
        </p>
        <button onClick={onClose} data-testid="listen-board-close"
                className="text-[11px] text-ink-soft hover:text-ink">Hide</button>
      </div>

      {board.friends === 0 ? (
        <p className="mt-2 text-[11px] text-ink-soft" data-testid="listen-board-empty">
          Start a chat with a friend and their listening run shows up here beside yours.
        </p>      ) : (
        <>
          {board.to_catch && (
            <p className="mt-2 text-[11px] text-ink-soft" data-testid="listen-board-catch">
              {board.to_catch.name} is on {board.to_catch.days} day
              {board.to_catch.days === 1 ? "" : "s"} — {board.to_catch.gap === 0
                ? "level with you" : `${board.to_catch.gap} ahead of you`}.
            </p>
          )}
          <ul className="mt-2 space-y-1">
            {board.people.map((p) => (
              <li key={`${p.rank}-${p.name}`}
                  data-testid={`listen-board-row-${p.rank}`}
                  className={`flex items-center gap-2 rounded-xl px-2 py-1 text-xs ${
                    p.is_me ? "bg-brand/10 text-ink" : "text-ink-soft"}`}>
                <span className="w-5 shrink-0 text-[11px] text-ink-soft">{p.rank}</span>
                <Headphones className={`h-3 w-3 shrink-0 ${p.today ? "text-emerald-300" : "opacity-40"}`} />
                <span className="min-w-0 flex-1 truncate">{p.is_me ? "You" : p.name}</span>
                <span className="shrink-0 font-semibold">
                  {p.days} day{p.days === 1 ? "" : "s"}
                </span>
                {p.best > p.days && (
                  <span className="shrink-0 text-[10px] text-ink-soft">best {p.best}</span>
                )}
                {!p.is_me && p.slipping && (
                  <button
                    data-testid={`cheer-${p.rank}`}
                    onClick={() => cheer(p)}
                    disabled={cheered.includes(p.name)}
                    title="Send a friendly poke"
                    className="inline-flex shrink-0 items-center gap-1 rounded-full border border-brand/40 px-2 py-0.5 text-[10px] font-semibold text-brand transition-colors hover:bg-brand/10 disabled:opacity-50"
                  >
                    <Hand className="h-3 w-3" /> {cheered.includes(p.name) ? "Cheered" : "Cheer"}
                  </button>
                )}
              </li>
            ))}
          </ul>
        </>
      )}
    </div>
  );
};
